/**
 * @file openPositions.component.tsx
 * @path apps/website/src/features/recruitment/components/openPositions.component.tsx
 * @description Pure presentational component for displaying vacancies.
 */

import { cn } from "@sirbastians/utils";
import { Button } from "@sirbastians/ui";
import { motion, AnimatePresence } from "framer-motion";
import { Vacancy } from "@sirbastians/types";
import { ChevronDown, EnvelopeAtFill, GeoAltFill } from "react-bootstrap-icons";
import { useRecruitmentFacade } from "../hooks/useRecruitmentFacade.hook";

const AccordionItem = ({
  job,
  isOpen,
  onToggle,
  onApply,
}: {
  job: Vacancy;
  isOpen: boolean;
  onToggle: () => void;
  onApply: () => void;
}) => (
  <div
    className={cn(
      "group border-2 transition-all duration-300 overflow-hidden",
      isOpen
        ? "border-accent bg-card"
        : "border-border bg-background hover:border-foreground/20",
    )}
  >
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between p-6 text-left outline-none"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <h4 className="text-xl font-montserrat font-bold text-foreground">
          {job.title}
        </h4>
        <span
          className={cn(
            "inline-flex items-center px-3 py-1 text-[10px] font-montserrat font-black uppercase tracking-widest border transition-colors",
            isOpen
              ? "bg-accent border-accent text-white"
              : "border-border text-muted-foreground",
          )}
        >
          {job.type}
        </span>
      </div>

      <div className="flex items-center gap-6">
        <div className="hidden sm:flex items-center gap-2 text-muted-foreground text-sm font-open-sans">
          <GeoAltFill className="h-4 w-4" />
          {job.location}
        </div>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-muted-foreground transition-transform duration-500",
            isOpen && "rotate-180 text-accent",
          )}
        />
      </div>
    </button>

    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
        >
          <div className="px-6 pb-8 space-y-6">
            <div className="h-px w-full bg-border" />
            <div className="space-y-4 max-w-3xl">
              <p className="sm:hidden flex items-center gap-2 text-accent text-xs font-bold uppercase tracking-wider">
                <GeoAltFill className="h-3 w-3" /> {job.location}
              </p>
              <p className="font-open-sans text-muted-foreground leading-relaxed text-base">
                {job.description}
              </p>
            </div>
            <Button
              variant="accent"
              onClick={onApply}
              className="rounded-none font-montserrat font-bold uppercase tracking-widest px-8"
            >
              <EnvelopeAtFill className="mr-2 h-4 w-4" />
              Apply Now
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

interface OpenPositionsProps {
  facade: ReturnType<typeof useRecruitmentFacade>["positions"];
}

export const OpenPositions = ({ facade }: OpenPositionsProps) => {
  const { data, openId, actions } = facade;

  return (
    <section id="open-positions" className="py-24 bg-background">
      <div className="px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-3">
          {/* Left: Section Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="space-y-2">
              <span className="text-accent font-montserrat font-bold tracking-[0.2em] text-xs uppercase">
                Careers
              </span>
              <h2 className="text-4xl font-montserrat font-bold tracking-tighter sm:text-5xl">
                Open Positions
              </h2>
            </div>
            <p className="text-lg font-open-sans text-muted-foreground leading-relaxed font-light">
              Join our mission to redefine luxury transport in Cape Town. We
              seek professionals who embody reliability, safety, and premium
              service.
            </p>
          </div>

          {/* Right: Job List */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {data.length > 0 ? (
              data.map((job) => (
                <AccordionItem
                  key={job.id}
                  job={job}
                  isOpen={openId === job.id}
                  onToggle={() => actions.toggleAccordion(job.id)}
                  onApply={() => actions.openApplication(job)}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center rounded-none border-2 border-dashed border-border p-16 text-center">
                <h3 className="text-xl font-montserrat font-bold text-foreground">
                  No Vacancies Found
                </h3>
                <p className="mt-2 font-open-sans text-muted-foreground">
                  Our fleet is currently full, but we're always growing. Check
                  back soon.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
